import { GameEngine, type ColorId, type Pos } from '@color-dots/game';
import type { CanvasRenderer } from './CanvasRenderer.js';

export type InputCallback = () => void;

export class InputController {
  private activeColor: ColorId | null = null;
  private isDrawing = false;
  private pointerId: number | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private renderer: CanvasRenderer,
    private engine: GameEngine,
    private onUpdate: InputCallback,
    private onWin: InputCallback,
  ) {
    this.bindEvents();
  }

  getActiveColor(): ColorId | null {
    return this.activeColor;
  }

  destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('lostpointercapture', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
  }

  private bindEvents(): void {
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('lostpointercapture', this.onPointerUp);
    window.addEventListener('keydown', this.onKeyDown);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.isDrawing) return;
    if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.engine.undo();
      this.onUpdate();
    }
  };

  private onPointerDown = (event: PointerEvent): void => {
    if (this.pointerId !== null) return;

    const pos = this.getPosFromEvent(event);
    if (!pos) return;

    this.pointerId = event.pointerId;
    this.canvas.setPointerCapture(event.pointerId);

    const endpointColor = this.engine.getColorAtEndpoint(pos);
    const cellColor = this.engine.getColorAtCell(pos);

    if (endpointColor) {
      this.activeColor = endpointColor;
      this.isDrawing = true;
      this.engine.startPath(endpointColor, pos);
      this.onUpdate();
      return;
    }

    if (cellColor) {
      this.activeColor = cellColor;
      this.isDrawing = true;
      return;
    }
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.isDrawing || this.pointerId !== event.pointerId || !this.activeColor) {
      return;
    }

    const pos = this.getPosFromEvent(event);
    if (!pos) return;

    const result = this.engine.extendPath(this.activeColor, pos);
    if (result.ok) {
      this.onUpdate();
      if (result.won) {
        this.isDrawing = false;
        this.onWin();
      }
    } else if (result.reason === 'invalid-move') {
      this.renderer.notifyGearInvalidMove(this.activeColor);
      this.onUpdate();
    }
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;

    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }

    this.isDrawing = false;
    this.pointerId = null;
  };

  private getPosFromEvent(event: PointerEvent): Pos | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    return this.renderer.pixelToPos(x, y, this.engine.getState().size);
  }
}
